import { useState, useEffect } from "react";
import { TempoChatsFilterBar } from "./TempoChatFilterBar";
import { TempoChatListItem } from "./TempoChatItem";
import { useTranslation } from "react-i18next";
import { useTempoChatListQuery, useTempoChatUnreadCounts, useGetTempoChatQuery } from "query/tempo-chat.query";
import { useTempoChatListFilterBar } from "./useTempoChatListFilterBar";
import { AlertCircleIcon, MessageSquareOff, SearchX } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import TempoChatModal from "components/TempoChat/TempoChatModal";
import { Chat } from "@acoladgroup/tempo-messaging-library";

const TempoChatListPage = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "messaging.tempo-chat-list-page",
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const chatId = searchParams.get("chat");
  const unreadOnlyParam = searchParams.get("unreadOnly");

  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [isUnreadOnly, setIsUnreadOnly] = useState<boolean>(unreadOnlyParam === "true");
  const [targetLanguageFilterValue, setTargetLanguageFilterValue] = useState<string>();
  const [chatsFilterValue, setChatsFilterValue] = useState<string>("all-chats");
  const [customerFilterValue, setCustomerFilterValue] = useState<string>();
  const [sortValue, setSortValue] = useState<string>("last-message");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const [tempoChatData, setTempoChatData] = useState<Chat | null>(null);
  const fetchTempoChat = useGetTempoChatQuery();

  const { data, isLoading, error } = useTempoChatListQuery();
  const { data: tempoChatUnreadCounts } = useTempoChatUnreadCounts();

  useEffect(() => {
    if (chatId) {
      fetchTempoChat(Number(chatId)).then(setTempoChatData);
    } else {
      setTempoChatData(null);
    }
  }, [chatId, fetchTempoChat]);

  useEffect(() => {
    setIsUnreadOnly(unreadOnlyParam === "true");
  }, [unreadOnlyParam]);

  const handleUnreadOnlyToggle = () => {
    const newValue = !isUnreadOnly;
    const newParams = new URLSearchParams(searchParams);

    if (newValue) {
      newParams.set("unreadOnly", "true");
    } else {
      newParams.delete("unreadOnly");
    }

    setSearchParams(newParams);
  };

  const closeModal = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("chat");
    setSearchParams(newParams);
    setTempoChatData(null);
  };

  const hasActiveFilters =
    (searchValue !== null && searchValue !== "") ||
    isUnreadOnly ||
    (targetLanguageFilterValue !== null && targetLanguageFilterValue !== undefined) ||
    chatsFilterValue !== "all-chats";

  const clearFilters = () => {
    setSearchValue(null);
    setTargetLanguageFilterValue(undefined);
    setChatsFilterValue("all-chats");

    const newParams = new URLSearchParams(searchParams);
    newParams.delete("unreadOnly");
    setSearchParams(newParams);
  };

  const { filteredAndSortedChats } = useTempoChatListFilterBar({
    tempoChats: data?.tempoChats,
    searchValue,
    isUnreadOnly,
    targetLanguageFilterValue,
    chatsFilterValue,
    customerFilterValue,
    sortValue,
    sortDirection,
    tempoChatUnreadCounts,
  });

  return (
    <div className="flex flex-col">
      <div className="text-color-primary bg-color-base mb-4 border-b border-slate-200 dark:border-slate-600">
        <div className="px-10 py-6">
          <h1 className="text-3xl font-medium leading-tight">{t("title")}</h1>
          <p className="text-color-secondary text-sm">{t("description")}</p>
          <div className="flex gap-7 rounded border bg-gray-50 p-3 text-sm text-gray-600">
            <AlertCircleIcon className="size-5" />
            <ul className="list-disc">
              <li>{t("disclamer-1")}</li>
              <li>{t("disclamer-2")}</li>
              <li>{t("disclamer-3")}</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="m-4">
        <TempoChatsFilterBar
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          isUnreadOnly={isUnreadOnly}
          onUnreadOnlyClick={handleUnreadOnlyToggle}
          targetLanguageFilterValue={targetLanguageFilterValue}
          onTargetLanguageFilterValueChange={setTargetLanguageFilterValue}
          chatsFilterValue={chatsFilterValue}
          onChatsFilterValueChange={setChatsFilterValue}
          customerFilterValue={customerFilterValue}
          onCustomerFilterValueChange={setCustomerFilterValue}
          sortValue={sortValue}
          onSortValueChange={setSortValue}
          sortDirection={sortDirection}
          onSortDirectionChange={setSortDirection}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
        />

        {error && (
          <div className="mt-6 text-center text-red-600">{t("error", "An error occurred while loading chats")}</div>
        )}
        {isLoading ? (
          <div className="mt-6 text-center">{t("loading", "Loading chats...")}</div>
        ) : (
          <div
            className="text-color-base bg-color-base rounded-md text-base shadow"
            data-test={`tempo-chats-card-container`}
          >
            {filteredAndSortedChats && filteredAndSortedChats.length === 0 ? (
              <div className="mt-6 text-center text-gray-600">
                {hasActiveFilters ? <NoResultMessage /> : <NoConversationsMessage />}
              </div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-slate-600" data-test={`tempo-chats-cards`}>
                {filteredAndSortedChats.map((chat) => (
                  <li key={chat.id}>
                    <TempoChatListItem chat={chat} unreadMessageCount={tempoChatUnreadCounts?.[chat.id]} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {tempoChatData && <TempoChatModal show={!!tempoChatData} tempoChatData={tempoChatData} onClose={closeModal} />}
    </div>
  );
};

function NoResultMessage() {
  const { t } = useTranslation("translation", { keyPrefix: "messaging.tempo-chat-list-page" });
  return (
    <div className="flex flex-col items-center p-16">
      <div className="flex w-1/2 flex-col items-center">
        <SearchX className="mb-4 size-16" />
        <div className="mb-2 text-xl font-semibold">{t("no-results-with-filters")}</div>
        <div className="text-base font-light">{t("no-results-with-filters-description")}</div>
      </div>
    </div>
  );
}

function NoConversationsMessage() {
  const { t } = useTranslation("translation", { keyPrefix: "messaging.tempo-chat-list-page" });
  return (
    <div className="flex flex-col items-center p-16">
      <div className="flex w-1/2 flex-col items-center">
        <MessageSquareOff className="mb-4 size-16" />
        <div className="mb-2 text-xl font-semibold">{t("no-chats-available")}</div>
        <div className="text-base font-light">{t("no-chats-available-description")}</div>
      </div>
    </div>
  );
}

export default TempoChatListPage;
